import axios from "axios";
import { useState } from "react";
import { toast } from "sonner";
import { IndianRupee, Trash2, Undo2 } from "lucide-react";
import { API } from "./shared";
import { LiveSpecEditor } from "./LiveSpecEditor";

const COPY = {
  revoke: { title: "Remove approval", note: "Bidding stops, all bids are voided, and the job goes back to the review queue.", btn: "Remove approval" },
  delete: { title: "Delete job permanently", note: "The job, its bids and files are erased for good. This cannot be undone.", btn: "Delete permanently" },
};

export function AdminJobActions({ job, bids, refresh, close }) {
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const canRevoke = ["live", "closed"].includes(job.status);
  const ok = reason.trim().length >= 3 && (mode !== "delete" || confirmText === "DELETE");
  const run = async () => {
    setBusy(true);
    try {
      if (mode === "revoke") {
        const r = await axios.post(`${API}/admin/jobs/${job.id}/revoke-approval`, { reason: reason.trim() });
        toast.success(`Approval removed · ${r.data.bids_voided} bid(s) voided`);
        setMode(null); setReason(""); close(); await refresh();
      } else {
        await axios.delete(`${API}/admin/jobs/${job.id}`, { data: { reason: reason.trim() } });
        toast.success("Job deleted permanently");
        close(); await refresh();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="admin-job-actions" style={{ border: "1px solid #FECACA", background: "#FFF7F7", borderRadius: 12, padding: 14, marginTop: 14 }}>
      <b style={{ fontSize: 13, color: "#7F1D1D" }}>Admin actions</b>
      {!mode ? (
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {canRevoke && job.status === "live" && !(bids || []).length && <LiveSpecEditor job={job} refresh={refresh} close={close}/>}
          {canRevoke && <button data-testid="job-set-floor-btn" className="so-btn so-blue" onClick={() => setMode("floor")}><IndianRupee size={14}/>{job.price_floor > 0 ? `Price floor ₹${Number(job.price_floor).toLocaleString("en-IN")}` : "Set price floor"}</button>}
          {canRevoke && <button data-testid="job-revoke-approval-btn" className="so-btn" onClick={() => setMode("revoke")}><Undo2 size={14}/>Remove approval</button>}
          <button data-testid="job-delete-btn" className="so-btn so-red" onClick={() => setMode("delete")}><Trash2 size={14}/>Delete job</button>
        </div>
      ) : mode === "floor" ? (
        <FloorForm job={job} bids={bids} done={async () => { setMode(null); close(); await refresh(); }} cancel={() => setMode(null)}/>
      ) : (
        <div data-testid={`job-${mode}-form`} style={{ marginTop: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{COPY[mode].title}</div>
          <div style={{ fontSize: 12, color: "#64748b", margin: "2px 0 8px" }}>{COPY[mode].note} The buyer will see your reason.</div>
          <textarea data-testid="job-action-reason" rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for the buyer (required)"
            style={{ width: "100%", border: "1px solid #CBD5E1", borderRadius: 8, padding: "7px 9px", fontSize: 13, fontFamily: "inherit" }}/>
          {mode === "delete" && (
            <input data-testid="job-delete-confirm-input" value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="Type DELETE to confirm"
              style={{ width: "100%", border: "1px solid #CBD5E1", borderRadius: 8, padding: "7px 9px", fontSize: 13, marginTop: 8 }}/>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button data-testid="job-action-confirm-btn" className={`so-btn ${mode === "delete" ? "so-red" : "so-blue"}`} disabled={!ok || busy} onClick={run}>{busy ? "Working…" : COPY[mode].btn}</button>
            <button data-testid="job-action-cancel-btn" className="so-btn" onClick={() => { setMode(null); setReason(""); setConfirmText(""); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function FloorForm({ job, bids, done, cancel }) {
  const [val, setVal] = useState(String(job.price_floor || ""));
  const [busy, setBusy] = useState(false);
  const n = Number(val);
  const below = n > 0 ? (bids || []).filter(b => Number(b.price) < n).length : 0;
  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/admin/jobs/${job.id}/price-floor`, { price_floor: n });
      toast.success(`Price floor set to ₹${r.data.price_floor.toLocaleString("en-IN")}${r.data.bids_below_floor ? ` · ${r.data.bids_below_floor} bid(s) flagged` : ""}`);
      await done();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not set the floor"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="job-floor-form" style={{ marginTop: 10 }}>
      <div style={{ fontWeight: 700, fontSize: 13 }}>Set price floor</div>
      <div style={{ fontSize: 12, color: "#64748b", margin: "2px 0 8px" }}>Minimum bid suppliers must offer. Suppliers see it on the job; use 0 to remove the floor. Bids below it are flagged and can't be awarded until raised.</div>
      <input data-testid="job-floor-input" type="number" min="0" value={val} onChange={e => setVal(e.target.value)} placeholder="Amount in ₹"
        style={{ width: 220, border: "1px solid #CBD5E1", borderRadius: 8, padding: "7px 9px", fontSize: 13 }}/>
      {below > 0 && <div data-testid="job-floor-below-preview" style={{ fontSize: 12, color: "#B91C1C", marginTop: 6 }}>{below} current bid(s) are below this amount; those suppliers will be asked to raise.</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button data-testid="job-floor-save-btn" className="so-btn so-blue" disabled={busy || val === "" || n < 0 || n === Number(job.price_floor || 0)} onClick={save}>{busy ? "Saving…" : "Save floor"}</button>
        <button data-testid="job-floor-cancel-btn" className="so-btn" onClick={cancel}>Cancel</button>
      </div>
    </div>
  );
}
